import React, { FC, useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import { Image, Spinner } from 'ui'
import { Table, TableBody, TableCell, TableRow } from '@material-ui/core'
import TablePagination from '@material-ui/core/TablePagination'
import EmptyComponent from 'components/Empty'
import ProtocolsSearchBar from './ProtocolsSearchBar'
import ConfirmationDialog from './ConfirmationDialog'
import useStore from 'store'
import { getProtocolPermissions } from './permissions'

import {
  Header,
  HeaderTitle,
  BackButton,
  TableBar,
  BackHeader,
  HeaderTitleContainer,
  BackHeaderText,
  HeaderTitle1,
  HeaderIcon,
  TitleMain,
  TitleSecond,
  TableHeadStyled,
  TableContainerStyled,
  TableCellStyled,
  TitleCell,
  CategoryBadge,
  DateCell,
  AuthorCell,
  VersionCell,
  RestoreButton,
  DeleteButton,
  ActionsCell,
  ActionImage,
} from './ArchiveList.styles'

import Icon4 from 'assets/icons/protocols/icon4.svg'
import Icon5 from 'assets/icons/protocols/icon5.svg'
import Icon6 from 'assets/icons/protocols/icon6.svg'
import RemoveIcon from 'assets/icons/protocols/icon7.svg'
import RestoreIcon from 'assets/icons/protocols/icon8.svg'
import FileIcon from 'assets/icons/ims/File.svg'

type Props = {
  onBack: () => void
}

const ArchiveList: FC<Props> = ({ onBack }) => {
  const { protocolsStore } = useStore()
  const [filterActive, setFilterActive] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [removeItemId, setRemoveItemId] = useState<number | null>(null)
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false)
  const [restoreItemId, setRestoreItemId] = useState<number | null>(null)

  useEffect(() => {
    protocolsStore.loadArchive(page + 1, rowsPerPage)
  }, [page, rowsPerPage])

  const { archiveItems, archiveTotalCount, loadingArchive } = protocolsStore
  const perms = getProtocolPermissions(protocolsStore.userPermissions)

  return (
    <>
      <ConfirmationDialog
        open={openConfirmDialog}
        closeModal={() => setOpenConfirmDialog(false)}
        onConfirm={async () => {
          await protocolsStore.deleteProtocol(removeItemId)
          setOpenConfirmDialog(false)
          protocolsStore.loadArchive(page + 1, rowsPerPage)
        }}
        TitleMain="מחיקת מסמך לצמיתות"
        TitleSecond={`האם אתה בטוח שברצונך למחוק לצמיתות מסמך זה?`}
        confirmBtnColor="#fb2c36"
        ConfirmButtonText={"מחק לצמיתות"}
        CancelButtonText={"בטל"}
      />
      <ConfirmationDialog
        open={openRestoreDialog}
        closeModal={() => setOpenRestoreDialog(false)}
        onConfirm={async () => {
          await protocolsStore.restoreProtocol(restoreItemId)
          setOpenRestoreDialog(false)
          protocolsStore.loadArchive(page + 1, rowsPerPage)
        }}
        TitleMain="שחזור מסמך"
        TitleSecond={`האם אתה בטוח שברצונך לשחזר את המסמך מהארכיון?`}
        confirmBtnColor="#00c951"
        ConfirmButtonText={"שחזר"}
        CancelButtonText={"בטל"}
      />
      <Header>
        <HeaderTitle>
          <HeaderTitleContainer>
            <HeaderIcon><Image src={Icon5} /></HeaderIcon>
            <HeaderTitle1>
              <TitleMain>ארכיון מסמכים</TitleMain>
              <TitleSecond>מסמכים שהועברו לארכיון • {archiveTotalCount} סה"כ</TitleSecond>
            </HeaderTitle1>
          </HeaderTitleContainer>
          <BackHeader>
            <BackHeaderText>חזרה למסך הקודם</BackHeaderText>
            <BackButton onClick={onBack}>
              <Image src={Icon4} />
            </BackButton>
          </BackHeader>
        </HeaderTitle>

        <ProtocolsSearchBar
          filterActive={filterActive}
          onFilterToggle={() => setFilterActive(!filterActive)}
          onSearch={setSearch}
        />
      </Header>

      <TableBar>
        <TableContainerStyled>
          <Table stickyHeader>
            <TableHeadStyled>
              <TableRow>
                <TableCell style={{ textAlign: 'right' }} align="right">כותרת</TableCell>
                <TableCell style={{ textAlign: 'right' }} align="right">קטגוריה</TableCell>
                <TableCell style={{ textAlign: 'right' }} align="right">כותב</TableCell>
                <TableCell style={{ textAlign: 'right' }} align="right">תאריך</TableCell>
                <TableCell style={{ textAlign: 'right' }} align="right">גרסה</TableCell>
                <TableCell style={{ textAlign: 'right' }} align="right">פעולות</TableCell>
              </TableRow>
            </TableHeadStyled>
            <TableBody>
              {loadingArchive && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Spinner />
                  </TableCell>
                </TableRow>
              )}
              {!loadingArchive && archiveItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <EmptyComponent text="לא נמצאו מסמכים בארכיון" />
                  </TableCell>
                </TableRow>
              )}
              {!loadingArchive && archiveItems.map(item => (
                <TableRow key={item.id} hover>
                  <TableCellStyled align="right">
                    <TitleCell>
                      <Image src={FileIcon} />
                      {item.title}
                    </TitleCell>
                  </TableCellStyled>
                  <TableCellStyled align="right">
                    <CategoryBadge>{item.categoryName}</CategoryBadge>
                  </TableCellStyled>
                  <TableCellStyled align="right">
                    <AuthorCell>{item.creatorName}</AuthorCell>
                  </TableCellStyled>
                  <TableCellStyled align="right">
                    <DateCell>
                      <Image src={Icon6} />
                      {item.protocolDate}
                    </DateCell>
                  </TableCellStyled>
                  <TableCellStyled align="right">
                    <VersionCell>{item.version}</VersionCell>
                  </TableCellStyled>
                  <TableCellStyled align="right">
                    <ActionsCell>
                      {perms.canRestoreProtocol && (
                        <RestoreButton startIcon={<ActionImage src={RestoreIcon}/>} onClick={() => { setRestoreItemId(item.id); setOpenRestoreDialog(true) }}> שחזר</RestoreButton>
                      )}
                      {perms.canDeleteArchivedProtocol && (
                        <DeleteButton startIcon={<ActionImage src={RemoveIcon}/>} onClick={() => { setRemoveItemId(item.id); setOpenConfirmDialog(true) }}> מחק</DeleteButton>
                      )}
                    </ActionsCell>
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainerStyled>
        <TablePagination
          component="div"
          count={archiveTotalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="שורות בעמוד"
        />
      </TableBar>
    </>
  )
}

export default observer(ArchiveList)
